// FTEDN Creator — Feedback Panel (wired to API)
import { useState } from 'react';
import { apiClient } from '../../hooks/useApi';
import { validateFeedback, type FeedbackType } from '../BetaProgram/beta.helpers';
import { useToast } from '../../components/ui/Toast';
import './FeedbackPanel.css';

interface FeedbackPanelProps {
  programId?: string;
}

export function FeedbackPanel({ programId = '' }: FeedbackPanelProps) {
  const [tab, setTab] = useState<FeedbackType>('thoughts');
  const [content, setContent] = useState('');
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const { addToast } = useToast();

  const tabs: { key: FeedbackType; label: string; icon: string }[] = [
    { key: 'thoughts', label: 'First Thoughts', icon: '💭' },
    { key: 'review', label: 'Review', icon: '⭐' },
    { key: 'notes', label: 'Notes', icon: '📝' },
  ];

  const handleSubmit = async () => {
    const validation = validateFeedback({ type: tab, content, programId, rating: rating || undefined });
    if (!validation.valid) {
      addToast({ type: 'warning', title: 'Missing info', message: validation.error });
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiClient.post('/feedback', {
        type: tab, content, programId,
        ...(tab === 'review' && rating > 0 ? { rating } : {}),
      });
      if (res.success) {
        addToast({ type: 'xp', title: '+25 XP', message: `${tab === 'thoughts' ? 'Thoughts' : tab === 'review' ? 'Review' : 'Notes'} submitted!` });
        setContent('');
        setRating(0);
      } else {
        addToast({ type: 'error', title: 'Error', message: 'Submission failed' });
      }
    } catch {
      addToast({ type: 'error', title: 'Error', message: 'Could not reach server' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="feedback-panel">
      <h3>Share Your Experience</h3>
      <div className="feedback-tabs">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`feedback-tab ${tab === t.key ? 'active' : ''}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="feedback-content">
        {tab === 'review' && (
          <div className="feedback-rating">
            {[1, 2, 3, 4, 5].map(star => (
              <button key={star} className={`star-btn ${star <= rating ? 'active' : ''}`}
                onClick={() => setRating(star)} aria-label={`Rate ${star} stars`}>
                ⭐
              </button>
            ))}
          </div>
        )}

        <textarea className="neo-input feedback-textarea" value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={tab === 'thoughts' ? "What's your first impression?" :
            tab === 'review' ? 'Rate texture, scent, effectiveness...' : 'Any notes for the team...'} />

        <div className="feedback-meta">
          <span className="char-count">{content.length}/5000</span>
        </div>

        <div className="feedback-media-bar">
          <button className="media-btn" title="Record voice note">🎤 Voice</button>
          <button className="media-btn" title="Upload photo">📷 Photo</button>
          <button className="media-btn" title="Record video">🎥 Video</button>
        </div>

        <button className="neo-btn neo-btn--primary feedback-submit" id="submit-feedback"
          onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Submitting...' : `Submit ${tab === 'thoughts' ? 'Thoughts' : tab === 'review' ? 'Review' : 'Notes'}`}
        </button>
      </div>
    </div>
  );
}
